import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { getNodeApiUrl } from "../config/api";
import { useCompany } from "../context/CompanyContext";

const ViewQudemo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useCompany();

  const [qudemo, setQudemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchQudemo();
  }, [id]);

  const fetchQudemo = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(getNodeApiUrl(`/api/qudemos/${id}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQudemo(data.data);
      } else {
        setError(data.error || "Failed to fetch qudemo");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getVideoType = (url) => {
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return "youtube";
    if (url.includes("loom.com")) return "loom";
    if (url.includes("vimeo.com")) return "vimeo";
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-10 w-10 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => navigate("/qudemos")}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Back to Qudemos
        </button>
      </div>
    );
  }

  if (!qudemo) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Qudemo Not Found
        </h3>
        <button
          onClick={() => navigate("/qudemos")}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Back to Qudemos
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/qudemos")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Qudemos
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 text-center">
          View Qudemo
        </h1>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[{ id: "overview", name: "Overview" }].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Title
                </label>
                <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                  {qudemo.title || "Untitled Qudemo"}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <PlayIcon className="h-5 w-5 text-blue-500" />
                    <span className="font-medium">Videos</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {qudemo.videos?.length || 0}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="font-medium">Status</span>
                  </div>
                  <p className="text-sm font-medium text-green-600">Active</p>
                </div>
              </div>

              {/* Quick Video Links Summary */}
              {qudemo.videos && qudemo.videos.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    All Video Links
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      {qudemo.videos.map((video, index) => (
                        <div
                          key={video.id || index}
                          className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-600 w-16">
                              Video {index + 1}:
                            </span>
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline break-all flex-1"
                            >
                              {video.video_url}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(video.video_url);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy link"
                            >
                              📋
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewQudemo;
